import { Form, useArrayField, useFieldState, useFormApi, useFormState } from "informedv4";
import { error } from "jquery";
import * as React from "react";
import { DeviceType, NotificationSubscription, Role, User } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { Checkbox } from "../forms/informedv4/CheckboxV4";
import { MultiSelect } from "../forms/informedv4/MultiSelectV4";
import { TextBox } from "../forms/informedv4/TextBoxV4";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import AsyncActionButton from "../misc/components/AsyncActionButton";
import { NotificationPlugin } from "./NotificationTypes";

export interface NotificationConfigProps {
    selectedTemplate: NotificationPlugin;
    existingSubscription?: undefined | NotificationSubscription;
    isEdit: boolean;
    onClose: () => void;
}

interface NotificationConfigFormState {
    users: User[];
    roles: Role[];
    rolesAndSites: Role[];
    notifyAuthor: boolean;
    notifySite: boolean;
    configurableParameters: any[];
}

interface CreateNotificationConfigParametersProps {
    selectedTemplate: NotificationPlugin;
}

export const CreateNotificationConfigParameters = ({ selectedTemplate }: CreateNotificationConfigParametersProps) => {
    const { render, arrayFieldApi, arrayFieldState } = useArrayField({
        name: "configurableParameters"
    } as any);

    return (
        <div className="row">
            {arrayFieldState.fields.map(({ field, key }, i) => (
                <div key={key} className="col-md-6">
                    <label>{(arrayFieldState.fields[i] as any).initialValue.name}</label>
                    <p className="help-block">{(arrayFieldState.fields[i] as any).initialValue.description}</p>
                    <TextBox name={`${field}.parameterValue`} id={`${field}.parameterValue`} field={`${field}.parameterValue`} type="number" min="1" max="100" required />
                </div>
            ))}
        </div>
    )
}

export const NotificationConfigForm = ({ selectedTemplate, existingSubscription, isEdit, onClose }: NotificationConfigProps) => {
    const [users, setUsers] = React.useState<User[]>();
    const [roles, setRoles] = React.useState<Role[]>();
    const [_, setDevices] = React.useState<DeviceType[]>();
    const [submitting, setSubmitting] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const client = useWebPortalApiClient();
    const formApi = useFormApi();
    const { study } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const getUsers = async () => {
            const result = await client.client.getUsers(undefined, `roles/any(r: r/studyId eq ${study.id}) or roles/any(r: r/studyId eq null)`);

            setUsers(result.value);

            const rolesResult = await client.client.getRoles(undefined, `studyId eq ${study.id}`);
            setRoles(rolesResult.value);

            const deviceResult = await client.client.getDeviceTypes();
            setDevices(deviceResult.value);
        }

        getUsers();

        return () => {
            client.abortController.abort();
        }
    }, []);

    const onSubmit = async () => {
        setSubmitting(true);
        setErrorAlert(null);
        formApi.validate();
        let state = formApi.getFormState();

        if ((state.values.users === null || (state.values.users as User[]).length === 0) &&
            (state.values.roles === null || (state.values.roles as Role[]).length === 0) &&
            (state.values.rolesAndSites === null || ((state.values.rolesAndSites as any[]).length === 0) &&
            state.values.notifySite === false &&
            state.values.notifyAuthor === false))

            formApi.setError("notificationError", "At least one notification option should be selected");

        state = formApi.getFormState();
        if (state.invalid) {
            setSubmitting(false);
            const notificationError = state.errors?.notificationError as string;
            return setErrorAlert(notificationError ?? "Errors exist on the page");
        }

        try {
            if (isEdit) {
                const patchResult = await client.client.patchNotificationSubscription({
                    studyId: study.id,
                    notifyImmediately: false,
                    notifyAuthor: state.values.notifyAuthor as boolean,
                    notifySite: state.values.notifySite as boolean,
                    usersToNotifyIds: (state.values.users as User[])?.map(u => u.id) ?? [],
                    rolesToNotifyIds: (state.values.roles as Role[])?.map(r => r.id) ?? [],
                    rolesWithSitesToNotifyIds: (state.values.rolesAndSites as Role[])?.map(r => r.id) ?? [],
                }, existingSubscription.id);
            } else {
                const postResult = await client.client.postNotificationSubscription({
                    studyId: study.id,
                    pluginId: selectedTemplate.Id,
                    pluginName: selectedTemplate.Name,
                    notifyImmediately: false,
                    notifyAuthor: state.values.notifyAuthor as boolean,
                    notifySite: state.values.notifySite as boolean,
                    usersToNotifyIds: (state.values.users as User[])?.map(u => u.id) ?? [],
                    rolesToNotifyIds: (state.values.roles as Role[])?.map(r => r.id) ?? [],
                    rolesWithSitesToNotifyIds: (state.values.rolesAndSites as Role[])?.map(r => r.id) ?? [],
                });
            }
        } catch (err) {
            setErrorAlert(err);
            return;
        }

        onClose();
        setSubmitting(false);
    }

    return (
        <>
            <div className="modal-body">
                <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                    {errorAlert}
                </Alert>
                <h2 className="text-primary">Configure Notification</h2>
                <div className="row">
                    
                    <div className="form-group col-md-6 col-sm-6">
                        <label>Individual Users to Notify</label>
                        <MultiSelect
                            name="users"
                            id="users"
                            field="users"
                            options={users}
                            labelKey="email"
                            valueKey="id"
                            multi
                        />
                    </div>
                    <div className="form-group col-md-6 col-sm-6">
                        <label>Roles to notify regardless of site</label>
                        <MultiSelect
                            name="roles"
                            id="roles"
                            field="roles"
                            options={roles}
                            labelKey="name"
                            valueKey="id"
                            multi
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-3 col-sm-3">
                        <Checkbox name="notifyAuthor" id="notifyAuthor" label="Notify the Author" field="notifyAuthor" />
                    </div>
                    <div className="form-group col-md-3 col-sm-3">
                        <Checkbox name="notifySite" id="notifySite" label="Notify users assigned to site" field="notifySite" />
                    </div>
                    <div className="form-group col-md-6 col-sm-6">
                        <label>Notify users assigned to site in roles</label>
                        <MultiSelect
                            name="rolesAndSites"
                            id="rolesAndSites"
                            field="rolesAndSites"
                            options={roles}
                            labelKey="name"
                            valueKey="id"
                            multi
                        />
                    </div>
                </div>
                <CreateNotificationConfigParameters selectedTemplate={selectedTemplate} />
            </div>
            <div className="modal-footer">
                <AsyncActionButton className="btn btn-primary" type="button" onClick={() => onSubmit()} isSubmitting={submitting}>Save</AsyncActionButton>
            </div>
        </>
    );
}

export const NotificationConfig = (props: NotificationConfigProps) => {
    let formInitialState = null;
    if (!props.existingSubscription) {
        formInitialState = {
            users: [],
            roles: [],
            rolesAndSites: [],
            notifySite: false,
            notifyAuthor: false,
            configurableParameters: props.selectedTemplate.ConfigurableParameters.map(st => ({
                name: st.Name,
                description: st.Description,
                parameterValue: 0
            }))
        }
    } else {
        formInitialState = {
            users: props.existingSubscription.usersToNotify,
            roles: props.existingSubscription.rolesToNotify,
            rolesAndSites: props.existingSubscription.rolesWithSitesToNotify,
            notifySite: props.existingSubscription.notifySite,
            notifyAuthor: props.existingSubscription.notifyAuthor,
            configurableParameters: props.selectedTemplate.ConfigurableParameters.map(st => ({
                name: st.Name,
                description: st.Description,
                parameterValue: 0
            }))
        }
    }

    return (
        <Form validateOn="submit" initialValues={formInitialState}>
            <NotificationConfigForm {...props} />
        </Form>
    )
}
